import { useState, useEffect } from "react";
import { Moon, Sun, Menu, X, ExternalLink } from "lucide-react";

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'light') {
      setDarkMode(false);
      document.documentElement.classList.remove('dark');
    } else {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <div className={`min-h-screen font-sans transition-colors duration-500 ${darkMode ? 'bg-black text-gray-300' : 'bg-white text-gray-900'}`}>

      {/* Navigation */}
      <header className={`border-b sticky top-0 z-50 transition-all duration-500 backdrop-blur-md ${
        scrollY > 50
          ? darkMode ? 'bg-black/90 border-gray-800' : 'bg-white/90 border-gray-200'
          : darkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-wider mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 group">
              <img
                src="/logo_light.svg"
                alt="Extheon Logo"
                className={`w-8 h-8 transform group-hover:scale-110 transition-transform duration-300 ${darkMode ? 'invert' : ''}`}
              />
              <span className="text-xl font-medium tracking-wide">Extheon</span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="https://correlationlab.com"
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm font-light transition-opacity hover:opacity-60 ${darkMode ? 'text-white' : 'text-gray-900'}`}
              >
                CorrelationLab
              </a>
              <button
                onClick={toggleTheme}
                className={`p-2 transition-all border ${darkMode ? 'bg-white border-white text-black hover:bg-gray-100' : 'bg-black border-black text-white hover:bg-gray-900'}`}
                aria-label="Toggle theme"
              >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </nav>

            <div className="md:hidden flex items-center gap-3">
              <button onClick={toggleTheme} className={`p-2 border ${darkMode ? 'bg-white border-white text-black' : 'bg-black border-black text-white'}`}>
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2">
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden border-t ${darkMode ? 'border-gray-800 bg-black' : 'border-gray-200 bg-white'}`}>
            <nav className="flex flex-col p-6 space-y-4">
              <a href="https://correlationlab.com" target="_blank" rel="noopener noreferrer" className="text-sm font-light">CorrelationLab</a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="max-w-wider mx-auto px-6">
          <div className="max-w-3xl">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-light leading-tight mb-6 tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Research, productized
            </h1>
            <p className={`text-lg mb-10 leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Extheon is how CorrelationLab technology reaches the market.
            </p>
            <a
              href="https://correlationlab.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center justify-center gap-2 px-6 py-3 font-medium border transition-colors ${
                darkMode
                  ? 'border-gray-700 text-white hover:bg-gray-900'
                  : 'border-gray-300 text-gray-900 hover:bg-gray-100'
              }`}
            >
              CorrelationLab
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Status */}
      <section className={`py-24 border-t ${darkMode ? 'border-gray-900' : 'border-gray-100'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="max-w-3xl">
            <div className="flex items-start gap-4">
              <div className={`w-2 h-2 rounded-full mt-2 ${darkMode ? 'bg-gray-500' : 'bg-gray-400'}`}></div>
              <div>
                <h3 className={`text-xl font-light mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  In Development
                </h3>
                <p className={`text-base ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  We're building our first products.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className={`py-32 border-t ${darkMode ? 'border-gray-900 bg-black' : 'border-gray-100 bg-white'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="max-w-3xl">
            <h2 className={`text-5xl font-light tracking-tight mb-8 leading-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Get in Touch
            </h2>
            <p className={`text-lg font-light mb-12 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Partnerships and inquiries.
            </p>
            <a
              href="mailto:hello@extheon.com"
              className={`inline-flex items-center gap-2 px-10 py-4 font-light transition-colors ${
                darkMode
                  ? 'bg-white text-black hover:bg-gray-100'
                  : 'bg-black text-white hover:bg-gray-900'
              }`}
            >
              hello@extheon.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-16 border-t ${darkMode ? 'border-gray-900 bg-black' : 'border-gray-100 bg-white'}`}>
        <div className="max-w-wider mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/logo_light.svg"
                  alt="Extheon Logo"
                  className={`w-6 h-6 ${darkMode ? 'invert' : ''}`}
                />
                <span className={`text-lg font-light ${darkMode ? 'text-white' : 'text-gray-900'}`}>Extheon</span>
              </div>
              <p className={`text-sm font-light ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                By CorrelationLab.
              </p>
            </div>

            <div>
              <div className={`font-medium mb-4 text-xs tracking-wider uppercase ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Parent</div>
              <div className="space-y-3 text-sm font-light">
                <a href="https://correlationlab.com" target="_blank" rel="noopener noreferrer" className={`block hover:opacity-70 transition-opacity ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>CorrelationLab</a>
              </div>
            </div>
          </div>

          <div className={`border-t pt-8 ${darkMode ? 'border-gray-900' : 'border-gray-100'}`}>
            <p className={`text-xs font-light ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              © 2025 Extheon. A division of CorrelationLab Scientific Research LLP.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
